import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';

import { buttonClasses, type ButtonSize, type ButtonVariant } from './buttonClasses';

/**
 * docs/02-DESIGN-SYSTEM.md §4.1.
 *
 * Five variants, three sizes, an icon slot on either side, and always
 * `border-radius: full` — the pill shape is not optional in this design.
 *
 * Renders an `<a>` when given `href`, because most call-to-action buttons in
 * the mockups navigate. A link that looks like a button must still be a link:
 * middle-click, "open in new tab" and the screen-reader rotor all depend on it.
 */
export type { ButtonSize, ButtonVariant } from './buttonClasses';

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Rendered before the label. Decorative — give the button a text label too. */
  iconLeft?: ReactNode;
  /** Rendered after the label. The mockups use ↗, ➤ and → here. */
  iconRight?: ReactNode;
  /** Stretches to the width of its container; used in the mobile drawer. */
  fullWidth?: boolean;
  children?: ReactNode;
  className?: string;
}

export interface ButtonProps
  extends CommonProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof CommonProps> {
  /** When set, the button renders as an anchor. */
  href?: string;
}

export const Button = forwardRef<HTMLButtonElement & HTMLAnchorElement, ButtonProps>(
  function Button(
    {
      variant = 'primary',
      size = 'md',
      iconLeft,
      iconRight,
      fullWidth = false,
      href,
      className,
      children,
      type,
      ...props
    },
    ref,
  ) {
    const classes = buttonClasses({ variant, size, fullWidth }, className);

    // Icons are decoration next to a text label, so they are hidden from
    // assistive technology (docs/02-DESIGN-SYSTEM.md §7.4 — colour and shape
    // are never the only carrier of meaning).
    const content = (
      <>
        {iconLeft ? (
          <span aria-hidden="true" className="shrink-0">
            {iconLeft}
          </span>
        ) : null}
        {children}
        {iconRight ? (
          <span aria-hidden="true" className="shrink-0">
            {iconRight}
          </span>
        ) : null}
      </>
    );

    if (href !== undefined) {
      const { disabled, ...rest } = props;
      // The prop surface is declared as a button's; the anchor branch accepts
      // the shared subset (events, aria, data-*).
      const anchorProps = rest as AnchorHTMLAttributes<HTMLAnchorElement>;

      return (
        <a
          ref={ref}
          // A disabled link is not a thing in HTML: removing href takes it out
          // of the tab order, and aria-disabled explains why it is inert.
          href={disabled ? undefined : href}
          aria-disabled={disabled ? true : undefined}
          className={classes}
          {...anchorProps}
        >
          {content}
        </a>
      );
    }

    return (
      <button ref={ref} type={type ?? 'button'} className={classes} {...props}>
        {content}
      </button>
    );
  },
);
